import {
  AnalyticsItemCategory,
  AnalyticsItemVariant,
} from "../analytics.types";
import {
  AnalyticsItemReturnData,
  BaseEvent,
  gaEventSelectItem,
  gaEventViewItem,
  gaEventViewItemList,
} from "../common.analytics";
import type {
  ApiPodcastModel,
  ApiPodcastEpisode,
} from "../../../domains/podcast/hooks/useGetPodcastsListQuery";
import { gaPushEvent } from "../base.analytics";

type PodcastItemPayload = {
  podcast: ApiPodcastModel;
  listName: string;
};

type PodcastEpisodeItemPayload = {
  episode: ApiPodcastEpisode;
  owner?: ApiPodcastModel["owner"];
  duration?: number;
  listName: string;
  percentage?: number;
  publicationUrl?: string;
  internal?: boolean;
};

type PodcastEpisodeItemListPayload = {
  podcast: ApiPodcastModel;
  episodes: ApiPodcastEpisode[];
  listName: string;
};

type PodcastEpisodeItemReturnData = AnalyticsItemReturnData & {
  audio_duration_ms?: number;
};

export function convertPodcastToItemData(
  payload: PodcastItemPayload
): AnalyticsItemReturnData {
  const { podcast, listName } = payload;
  return {
    item_id: podcast.id,
    item_name: podcast.title,
    item_brand: podcast.owner?.name ?? null,
    item_category: podcast.medical_specialties.map((spe) => spe.uid).join(","),
    item_category2: AnalyticsItemCategory.STANDARD,
    item_variant: AnalyticsItemVariant.PODCAST,
    item_list_name: listName,
    quantity: podcast.episodes.length,
  };
}

export function convertPodcastEpisodeToItemData(
  payload: PodcastEpisodeItemPayload
): PodcastEpisodeItemReturnData {
  const { episode, owner, listName, duration } = payload;
  return {
    item_id: episode.id,
    item_name: episode.title,
    item_brand: owner?.name ?? null,
    item_category: episode.medical_specialties.map((spe) => spe.uid).join(","),
    item_category2: AnalyticsItemCategory.STANDARD,
    item_variant: AnalyticsItemVariant.PODCAST_EPISODE,
    item_list_name: listName,
    audio_duration_ms: duration ? duration * 1000 : undefined,
  };
}

export function gaEventSelectItemPodcast(payload: PodcastItemPayload) {
  gaEventSelectItem(convertPodcastToItemData(payload));
}

export function gaEventSelectItemPodcastEpisode(
  payload: PodcastEpisodeItemPayload
) {
  gaEventSelectItem(convertPodcastEpisodeToItemData(payload));
}

export function gaEventViewItemPodcast(payload: PodcastItemPayload) {
  // Podcast attribution is OUT OF SCOPE: its `view_item` is emitted by the
  // global audio player, not by a route, so it does not follow a navigation the
  // way every other kind does. It needs its own design, not a seat here.
  gaEventViewItem(convertPodcastToItemData(payload), null);
}

export function gaEventViewItemPodcastEpisode(
  payload: PodcastEpisodeItemPayload
) {
  // Podcast attribution is OUT OF SCOPE: its `view_item` is emitted by the
  // global audio player, not by a route, so it does not follow a navigation the
  // way every other kind does. It needs its own design, not a seat here.
  gaEventViewItem(convertPodcastEpisodeToItemData(payload), null);
}

export function gaEventViewItemListPodcastEpisodes(
  payload: PodcastEpisodeItemListPayload
) {
  gaEventViewItemList(
    payload.episodes.map((episode) => {
      return convertPodcastEpisodeToItemData({
        episode,
        owner: payload.podcast.owner,
        listName: payload.listName,
      });
    })
  );
}

export function gaEventPlayPodcastEpisode(payload: PodcastEpisodeItemPayload) {
  gaPushEvent(BaseEvent.AUDIO_PLAYED, {
    is_public: true,
    internal: payload.internal ?? false,
    ecommerce: {
      items: [convertPodcastEpisodeToItemData(payload)],
    },
  });
}

export function gaEventPodcastEpisodeAudioProgress(
  payload: PodcastEpisodeItemPayload
) {
  gaPushEvent(BaseEvent.AUDIO_PROGRESS, {
    is_public: true,
    percentage: payload.percentage,
    internal: payload.internal ?? false,
    ecommerce: {
      items: [convertPodcastEpisodeToItemData(payload)],
    },
  });
}

export function gaEventPodcastEpisodeAccessOriginalPublication(
  payload: PodcastEpisodeItemPayload
) {
  gaPushEvent(BaseEvent.VIEW_ORIGINAL_PAPER, {
    is_public: true,
    publication_url: payload.publicationUrl,
    internal: payload.internal ?? false,
    ecommerce: {
      items: [convertPodcastEpisodeToItemData(payload)],
    },
  });
}
